<template>
  <FeaturesMothersFathersDay2025
    :is-fathers-day="isFathersDay()"
    :gift-set-products="giftSetProducts"
    :recommend-products="recommendProducts"
  />
</template>

<script setup lang="ts">
import { calcPrice } from "@tential/ec-gql-schema/utils/functions/price"
import { useCustomMeta } from "~/composables/meta"
import {
  RecommendProduct,
  giftSetProductsForMothersDay,
  giftSetProductsForFathersDay,
  GiftSetProduct,
  mothersDayRecommendProducts,
  fathersDayRecommendProducts,
  APIResponse,
} from "~/utils/functions/MothersFathersDay2025"
import { isSkuOutOfStock } from "~/utils/sku"
import { getSiteProductPageUrl, isAllOutOfStock } from "~/utils/functions/product"
import { useCampaign } from "~/composables/useCampaign"

definePageMeta({ layout: "default-with-extended-footer" })

const { isMothersAndFathersCampaign, isFathersDay } = useCampaign()

// キャンペーン期間外の場合は404表示
if (!isMothersAndFathersCampaign()) {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found", fatal: process.client })
}

const customMeta = useCustomMeta()
const config = useRuntimeConfig()
const BASE_URL = config.public.BASE_URL

const recommendProducts = ref<RecommendProduct[]>(
  isFathersDay() ? fathersDayRecommendProducts : mothersDayRecommendProducts,
)

const giftSetProducts = ref<GiftSetProduct[]>(
  isFathersDay() ? giftSetProductsForFathersDay : giftSetProductsForMothersDay,
)
useHead(
  customMeta.fetch({
    title: "TENTIAL Mother’s & Father’s Day Campaign",
    description: "",
    image: `${BASE_URL}/images/site/feature/mothers-fathers-day-2025/${
      isFathersDay() ? "fathers-day-" : ""
    }ogp-image.png`,
  }),
)

// このページパーツに含まれている商品を取得する
const loadProducts = async () => {
  const productIds = giftSetProducts.value.flatMap((item) =>
    item.products.flatMap((product) => product.selectItems.map((p) => p.documentId)),
  )
  const recommendProductIds = recommendProducts.value.map((product) => product.document_id)
  const noveltySkuIds = giftSetProducts.value
    .flatMap((item) => item.novelty?.skuIds)
    .filter((id): id is string => id !== undefined)

  const response = await $fetch<APIResponse>(
    `/api/_c/page/features/mothers-fathers-day-2025?product_ids=${Array.from(
      new Set(productIds.concat(recommendProductIds)),
    ).join(",")}&sku_ids=${noveltySkuIds.join(",")}`,
    {
      method: "GET",
    },
  )

  const products = response.products
  const skus = response.skus

  recommendProducts.value.forEach((product) => {
    const foundProduct = products.find((p) => p.document_id === product.document_id)
    if (foundProduct) {
      // 在庫情報、金額、URLを設定する
      product.isInStock = !isAllOutOfStock(foundProduct)
      product.price = calcPrice(foundProduct.skus[0]).original_reference_price
      product.url = getSiteProductPageUrl(foundProduct)
    }
  })

  giftSetProducts.value.forEach((item) => {
    // 在庫情報と金額を設定する
    item.products.forEach((productGroup) => {
      productGroup.selectItems.forEach((product) => {
        const foundProduct = products.find((p) => p.document_id === product.documentId)
        if (foundProduct) {
          if (product.skuName) {
            const sku = foundProduct.skus.find((sku) => sku.size === product.skuName)
            if (sku) {
              product.isInStock = !isSkuOutOfStock(sku)
              product.price = calcPrice(sku).original_reference_price
            }
          } else {
            product.isInStock = !isAllOutOfStock(foundProduct)
            product.price = calcPrice(foundProduct.skus[0]).original_reference_price
          }
        } else {
          product.isInStock = false
        }
        if (!product.isInStock) {
          product.disabled = true
          product.selectName += "(完売)"
        }
      })
    })

    // ノベルティの在庫情報を設定
    if (item.novelty) {
      item.novelty.isInStock = item.novelty.skuIds.some((skuId) => {
        const sku = skus.find((sku) => sku.document_id === skuId)
        return sku && !isSkuOutOfStock(sku)
      })
    }

    // 在庫がある商品を初期選択にする
    item.selectedProducts.forEach((selectedProduct, i) => {
      const productGroup = item.products[i]
      const selectedItem = productGroup.selectItems[Number(selectedProduct)]
      if (!selectedItem.isInStock) {
        const inStockProduct = productGroup.selectItems.find((item) => item.isInStock)
        if (inStockProduct) {
          item.selectedProducts[i] = inStockProduct.index.toString()
        }
      }
    })
  })
}

await useLazyAsyncData("loadProducts", async () => {
  await loadProducts()
})
</script>
